import { DsIconButton, DsPagination } from '@frontends/ds';
import { format, getMonth } from 'date-fns';
import * as htmlToImage from 'html-to-image';
import { t as i18nT } from 'i18next';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntityConfig } from '@/hooks/useEntityConfig';
import { type CertificateWithdrawal } from '../CertificatesLayout';
import { CertificatesTabs } from './Modules/CertificatesTabs';

const IMAGE_GENERATOR_TARGET_SIZE = 1000;

const CertificateImages: Record<string, { default: string }> = import.meta.glob(
  '/src/assets/images/certificates/**/*.png',
  { eager: true },
);

export function CertificatesList({
  certificates = [],
  certificatesIB = [],
}: {
  certificates: CertificateWithdrawal[];
  certificatesIB: CertificateWithdrawal[];
}) {
  const { t } = useTranslation();
  const config = useEntityConfig();
  const maxItemsPerPage = 12;
  const [activeTab, setActiveTab] = useState('certificates_tab_withdrawal');
  const [page, setPage] = useState(1);
  const tabs = useMemo(
    () => [
      { value: 'certificates_tab_withdrawal', label: t('certificates_tab_withdrawal') },
      { value: 'certificates_tab_rebates', label: t('certificates_tab_rebates') },
    ],
    [t],
  );

  useEffect(() => {
    if (certificates.length > 0) {
      setActiveTab('certificates_tab_withdrawal');
    } else if (certificatesIB.length > 0) {
      setActiveTab('certificates_tab_rebates');
    }
  }, [certificates, certificatesIB]);

  const currentCertificates = useMemo(
    () => (activeTab === 'certificates_tab_withdrawal' ? certificates : certificatesIB),
    [activeTab, certificates, certificatesIB],
  );

  const pagesCount = useMemo(
    () => Math.max(1, Math.ceil(currentCertificates.length / maxItemsPerPage)),
    [currentCertificates.length],
  );

  useEffect(() => {
    if (page > pagesCount) {
      setPage(pagesCount);
    }
  }, [page, pagesCount]);

  const paginatedCertificates = useMemo(() => {
    const start = (page - 1) * maxItemsPerPage;
    const end = start + maxItemsPerPage;

    return currentCertificates.slice(start, end);
  }, [currentCertificates, page]);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    setPage(1);
  }, []);

  const hasCertificates = currentCertificates.length > 0;
  const shouldShowTabs = certificatesIB.length > 0 && certificates.length > 0;
  const shouldShowPagination = hasCertificates && currentCertificates.length > maxItemsPerPage;

  const gridContent = hasCertificates ? (
    <>
      {shouldShowPagination && (
        <div className="mb-5">
          <DsPagination
            className="flex-wrap p-0 max-md:flex-col max-md:items-start"
            count={currentCertificates.length}
            pageSize={maxItemsPerPage}
            page={page}
            siblingCount={0}
            onPageChange={setPage}
          />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 4xl:grid-cols-5 gap-5">
        {paginatedCertificates.map((item) => (
          <CertificateCard
            key={item.uuid}
            entity={config.VITE_ENTITY}
            item={item}
            isIbCardsContent={activeTab === 'certificates_tab_rebates'}
          />
        ))}
      </div>
    </>
  ) : (
    <div className="flex min-h-[150px] w-full items-center justify-center rounded-2xl border border-dashed border-borderPrimary px-4 py-8 text-center text-base text-textSecondary">
      {t('certificates_empty_state')}
    </div>
  );

  return (
    <div>
      <div>
        {shouldShowTabs ? (
          <CertificatesTabs activeTab={activeTab} onTabChange={handleTabChange} tabs={tabs}>
            {gridContent}
          </CertificatesTabs>
        ) : (
          gridContent
        )}
      </div>
    </div>
  );
}

function CertificateCard({
  entity,
  item,
  isIbCardsContent,
}: {
  entity: string;
  item: CertificateWithdrawal;
  isIbCardsContent: boolean;
}) {
  const { t } = useTranslation();
  const certificateRef = React.useRef<HTMLDivElement>(null);

  const handleDownload = async () => {
    await imageGeneratorDownload(certificateRef);
  };
  const handleOpenInNewWindow = async () => {
    await imageGeneratorOpenInNewWindow(certificateRef);
  };
  const handleShare = async () => {
    await imageGeneratorShare(certificateRef);
  };

  return (
    <div className="group h-fit min-w-0 self-start">
      <div className="flex flex-col overflow-hidden rounded-[24px] border border-borderSurface bg-backgroundRaised text-textPrimary shadow-[0_8px_25px_-15px_rgba(9,18,36,0.35)] transition-[translate,box-shadow] duration-300 ease-out motion-safe:group-hover:-translate-y-1.5 group-hover:shadow-[0_16px_40px_-20px_rgba(9,18,36,0.45)] dark:group-hover:ring-1 dark:group-hover:ring-borderPrimary">
        <div className="@container relative w-full aspect-square overflow-hidden cursor-default select-none">
          <div className="absolute top-0 left-0 origin-top-left scale-[tan(atan2(100cqw,350px))]">
            {isIbCardsContent ? (
              <CertificateContentIB ref={certificateRef} entity={entity} item={item} />
            ) : (
              <CertificateContent ref={certificateRef} entity={entity} item={item} />
            )}
          </div>
        </div>
        <div className="flex px-3 py-3 items-center gap-2 text-base text-textSecondary">
          <DsIconButton name="download" aria-label={t('download')} onPress={handleDownload} />
          <DsIconButton name="external-link" aria-label={t('open_in_new_window')} onPress={handleOpenInNewWindow} />
          <DsIconButton name="share-2" aria-label={t('share')} onPress={handleShare} />
        </div>
      </div>
    </div>
  );
}

function processedDateOf(meta: CertificateWithdrawal['meta']): Date {
  return meta.processed_at ? new Date(meta.processed_at) : new Date();
}

const CertificateContent = React.forwardRef<HTMLDivElement, { entity: string; item: CertificateWithdrawal }>(
  ({ entity, item }, ref) => {
    const { t } = useTranslation();
    const { meta } = item;
    const processedDate = processedDateOf(meta);
    const processedAtPresentation = format(processedDate, 'd MMM yyyy, h:mm a');
    const amountPresentation = formatCurrency(item.amount);

    const backgroundImagePath = `/src/assets/images/certificates/${entity}/background-withdrawal.png`;
    const backgroundImage = CertificateImages[backgroundImagePath]?.default;

    return (
      <div ref={ref} className="relative h-[350px] w-[350px] box-sizing border-box aspect-square overflow-hidden">
        {backgroundImage && (
          <img
            src={backgroundImage}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover"
            crossOrigin="anonymous"
            decoding="sync"
            loading="eager"
            data-certificate-image="true"
          />
        )}
        <div className="absolute top-[16%] font-bold left-0 right-0 text-center text-white text-[25px]">
          {t('certificate_instant')}
        </div>
        <div className="absolute top-[25%] font-bold left-0 right-0 text-center text-white text-[25px]">
          {t('certificate_withdrawal')}
        </div>
        <div className="absolute top-[34%] font-bold left-0 right-0 text-center text-white text-[25px]">
          {t('certificate')}
        </div>
        <div className="absolute top-[44%] left-0 right-0 text-center text-white! text-[38px] font-black">
          {amountPresentation}
        </div>
        <div className="absolute top-[57.5%] font-semibold left-0 right-0 text-center text-white text-[9px]">
          {t('certificate_amount_withdrawn')}
        </div>
        <div className="absolute top-[87.5%] opacity-70 left-0 right-0 text-center text-white text-[8px]">
          {t('certificate_processed_instantly')}
        </div>
        <div className="absolute top-[91%] font-semibold left-0 right-0 text-center text-white text-[8px]">
          {processedAtPresentation}
        </div>
      </div>
    );
  },
);

const CertificateContentIB = React.forwardRef<HTMLDivElement, { entity: string; item: CertificateWithdrawal }>(
  ({ entity, item }, ref) => {
    const { t } = useTranslation();
    const { meta } = item;
    const processedDate = processedDateOf(meta);
    const processedAtPresentation = format(processedDate, 'd MMM yyyy, h:mm a');
    const processedAtMonth = getMonth(processedDate) + 1;
    const amountPresentation = formatCurrency(item.amount);

    let gradientColor = 'text-white';

    if (entity === 'quomarkets') {
      gradientColor = 'bg-gradient-to-r from-orange-400 via-brandPrimary to-orange-400';
    } else if (entity === 'tradequo') {
      gradientColor = 'bg-gradient-to-r from-brandPrimary via-rose-300 to-brandPrimary';
    }

    let imagelvl: string;

    switch (true) {
      case item.amount >= 100000:
        imagelvl = 'lvl-8.png'; // Royal League Partner
        break;
      case item.amount >= 50000:
        imagelvl = 'lvl-7.png'; // Ambassador
        break;
      case item.amount >= 30000:
        imagelvl = 'lvl-6.png'; // Diamond
        break;
      case item.amount >= 20000:
        imagelvl = 'lvl-5.png'; // Crystal
        break;
      case item.amount >= 10000:
        imagelvl = 'lvl-4.png'; // Platinium Elite
        break;
      case item.amount >= 5000:
        imagelvl = 'lvl-3.png'; // Platinium
        break;
      case item.amount >= 3000:
        imagelvl = 'lvl-2.png'; // Gold
        break;
      case item.amount >= 1000:
        imagelvl = 'lvl-1.png'; // VIP Partner
        break;
      default:
        imagelvl = 'lvl-1.png';
        break;
    }

    const backgroundImagePath = `/src/assets/images/certificates/${entity}/background-rebate.png`;
    const backgroundImage = CertificateImages[backgroundImagePath]?.default;

    const levelImagePath = `/src/assets/images/certificates/${entity}/${imagelvl}`;
    const levelImage = CertificateImages[levelImagePath]?.default;

    return (
      <div ref={ref} className="relative h-[350px] w-[350px] box-sizing border-box aspect-square overflow-hidden">
        {backgroundImage && (
          <img
            src={backgroundImage}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover"
            crossOrigin="anonymous"
            decoding="sync"
            loading="eager"
            data-certificate-image="true"
          />
        )}
        <div className="absolute top-[8%] font-semibold left-0 right-0 text-center text-white text-[20px]">
          {t('certificate_title_congratulations')},
        </div>
        <div className="absolute top-[14.5%] font-semibold left-0 right-0 text-center text-[20px]">
          <span className={`${gradientColor} bg-clip-text text-transparent`}>
            {t('certificate_month_rank', { month: t(`month_${processedAtMonth}`) })}
          </span>
        </div>
        {levelImage && (
          <img
            src={levelImage}
            alt=""
            aria-hidden
            className="absolute top-[22%] left-1/2 transform -translate-x-1/2 w-[65%]"
            crossOrigin="anonymous"
            decoding="sync"
            loading="eager"
            data-certificate-image="true"
          />
        )}
        <div className="absolute top-[48%] left-0 right-0 text-center text-textOnBrandPrimary! text-[34px] font-bold">
          {amountPresentation}
        </div>
        <div className="absolute top-[60%] font-semibold left-0 right-0 text-center text-textOnBrandPrimary! text-[9px]">
          {t('certificate_commision_payouts')}
        </div>
        <div className="absolute top-[87.5%] opacity-70 left-0 right-0 text-center text-white text-[8px]">
          {t('certificate_processed')}
        </div>
        <div className="absolute top-[91%] font-semibold left-0 right-0 text-center text-white text-[8px]">
          {processedAtPresentation}
        </div>
      </div>
    );
  },
);

CertificateContent.displayName = 'CertificateContent';
CertificateContentIB.displayName = 'CertificateContentIB';

async function imageGeneratorOpenInNewWindow(ref: React.RefObject<HTMLDivElement | null>) {
  const dataUrl = await imageGeneratorHandler(ref);
  const newWindow = window.open('', '_blank');

  if (newWindow) {
    const img = document.createElement('img');
    img.src = dataUrl;
    img.alt = i18nT('certificate');
    img.style.width = `100%`;
    img.style.height = `100%`;
    img.style.objectFit = 'contain';
    newWindow.document.body.appendChild(img);
  }
}

async function imageGeneratorDownload(ref: React.RefObject<HTMLDivElement | null>) {
  const dataUrl = await imageGeneratorHandler(ref);
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = `${i18nT('certificate')}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

async function imageGeneratorShare(ref: React.RefObject<HTMLDivElement | null>) {
  const dataUrl = await imageGeneratorHandler(ref);
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const file = new File([blob], `${i18nT('certificate')}.png`, { type: 'image/png' });

  if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({
      title: i18nT('certificate'),
      files: [file],
    });

    return { success: true, method: 'native' };
  }

  if (navigator.clipboard && (ClipboardItem as any)) {
    await navigator.clipboard.write([
      new ClipboardItem({
        'image/png': blob,
      }),
    ]);

    return { success: true, method: 'clipboard' };
  }
}

async function waitForImageLoad(img: HTMLImageElement): Promise<void> {
  if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, _reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      resolve();
    }, 10000);

    const cleanup = () => {
      clearTimeout(timeout);
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };

    const handleLoad = () => {
      cleanup();
      resolve();
    };

    const handleError = () => {
      cleanup();
      resolve();
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);

    if (typeof img.decode === 'function') {
      img
        .decode()
        .then(() => {
          if (img.complete && img.naturalWidth > 0) {
            cleanup();
            resolve();
          }
        })
        .catch(() => undefined);
    }
  });
}

async function ensureAllImagesLoaded(el: HTMLElement): Promise<void> {
  try {
    await (document as any).fonts?.ready;
  } catch {}

  const imgs = Array.from(el.querySelectorAll('img[data-certificate-image]')) as HTMLImageElement[];

  if (imgs.length === 0) {
    return;
  }

  await Promise.all(imgs.map((img) => waitForImageLoad(img)));

  await new Promise((resolve) => setTimeout(resolve, 200));

  try {
    await (document as any).fonts?.ready;
  } catch {}
}

async function imageGeneratorHandler(ref: React.RefObject<HTMLDivElement | null>): Promise<string> {
  const el = ref.current;

  if (!el) {
    throw new Error('Certificate element not found');
  }

  await ensureAllImagesLoaded(el);

  const { offsetWidth: width, offsetHeight: height } = el;
  const scale = IMAGE_GENERATOR_TARGET_SIZE / width;

  const imageOptions = {
    cacheBust: true,
    pixelRatio: 1,
    width: IMAGE_GENERATOR_TARGET_SIZE,
    height: IMAGE_GENERATOR_TARGET_SIZE,
    style: {
      width: `${width}px`,
      height: `${height}px`,
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
    },
    skipFonts: true,
  };

  try {
    await htmlToImage.toBlob(el, imageOptions);
    await new Promise((resolve) => setTimeout(resolve, 100));
  } catch {}

  try {
    const blob = await htmlToImage.toBlob(el, imageOptions);

    if (blob) {
      const url = URL.createObjectURL(blob);

      return url;
    }
  } catch (error) {
    console.warn('Failed to generate image via toBlob, trying toPng:', error);
  }

  try {
    const dataUrl = await htmlToImage.toPng(el, imageOptions);

    return dataUrl;
  } catch (error) {
    console.error('Failed to generate certificate image:', error);
    throw new Error('Failed to generate certificate image', { cause: error });
  }
}

function formatCurrency(value: number) {
  const valueRounded = Math.floor(value);

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    useGrouping: false,
  }).format(valueRounded);
}
